import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Collapse, OverlayTrigger, Spinner, Table, Tooltip } from 'react-bootstrap';
import * as d3 from 'd3';
import { fetchDashboardAnalytics } from '../api';
import { getSurgeryTypeColor } from '../utils/surgeryTypeColors';
import './styles/colors.css';

const MED_MANAGEMENT_COLOR = '#F0C6C6';
const MED_MANAGEMENT_BORDER = '#8F2020';
const JOINT_DECISIONS_COLOR = '#C6DAF0';
const SUCCESS_BG = 'rgba(92, 153, 110, 0.12)';
const SUCCESS_BORDER = '#2d6a3e';
const CARD_MUTED = '#5c6c7d';

function SurgeryTypePieChart({ data, width = 120, height = 120 }) {
  const svgRef = useRef();
  useEffect(() => {
    if (!svgRef.current || !data || data.length === 0) return;
    d3.select(svgRef.current).selectAll('*').remove();
    const sortedData = [...data].sort((a, b) => a.label.localeCompare(b.label));
    const pieLabels = sortedData.map((d) => d.label);
    const pie = d3.pie().value((d) => d.count)(sortedData);
    const arc = d3.arc().innerRadius(0).outerRadius(Math.min(width, height) / 2 - 4);
    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);
    svg
      .selectAll('path')
      .data(pie)
      .join('path')
      .attr('d', arc)
      .attr('fill', (d) => getSurgeryTypeColor(d.data.label, { labels: pieLabels }))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);
  }, [data, width, height]);
  return <svg ref={svgRef} />;
}

function formatNumber(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number' && !Number.isInteger(v)) return v.toFixed(2);
  return String(v);
}

/** Small “?” next to a stat label; hover (or focus) shows annotation text */
function StatHint({ text, hintKey }) {
  if (!text) return null;
  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 200, hide: 150 }}
      overlay={
        <Tooltip id={`stat-hint-${hintKey}`} className="text-start" style={{ maxWidth: 320 }}>
          <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
        </Tooltip>
      }
    >
      <span
        role="button"
        tabIndex={0}
        className="ms-1 rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          width: 16,
          height: 16,
          fontSize: 10,
          fontWeight: 700,
          lineHeight: 1,
          border: '1px solid #8fa4b8',
          color: '#205c8f',
          background: 'rgba(198, 218, 240, 0.5)',
          cursor: 'help',
        }}
        aria-label="Explanation"
      >
        ?
      </span>
    </OverlayTrigger>
  );
}

/** Compact scoreboard card: optional green “perfect score” state */
function MetricScoreCard({
  title,
  hero,
  heroSuffix = '',
  subtitle,
  stats = [],
  highlightSuccess = false,
  wide = false,
  children,
}) {
  const borderColor = highlightSuccess ? SUCCESS_BORDER : MED_MANAGEMENT_BORDER;
  const bg = highlightSuccess ? SUCCESS_BG : '#fff';
  const topAccent = highlightSuccess ? SUCCESS_BORDER : MED_MANAGEMENT_BORDER;

  return (
    <div className={wide ? 'col-12' : 'col-md-6 col-xl-4'}>
      <div
        className="h-100 rounded-3 shadow-sm overflow-hidden d-flex flex-column"
        style={{
          border: `1px solid ${borderColor}`,
          background: bg,
          minHeight: 168,
        }}
      >
        <div
          className="px-3 py-2 border-bottom d-flex align-items-start justify-content-between gap-2"
          style={{
            background: highlightSuccess ? 'rgba(92, 153, 110, 0.22)' : MED_MANAGEMENT_COLOR,
            borderColor: `${topAccent}33`,
          }}
        >
          <div className="min-w-0">
            <div className="small fw-semibold text-uppercase" style={{ letterSpacing: '0.04em', color: '#1a2b3c' }}>
              {title}
            </div>
            {subtitle && (
              <div className="text-muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>
                {subtitle}
              </div>
            )}
          </div>
          {highlightSuccess && (
            <span className="badge rounded-pill flex-shrink-0" style={{ backgroundColor: SUCCESS_BORDER, fontSize: '0.65rem' }}>
              100% match
            </span>
          )}
        </div>
        <div className="px-3 py-3 flex-grow-1 d-flex flex-column">
          {hero !== undefined && hero !== null && (
            <div className="mb-2">
              <span
                className="fw-bold lh-1 d-inline-block"
                style={{
                  fontSize: '2.25rem',
                  color: highlightSuccess ? SUCCESS_BORDER : MED_MANAGEMENT_BORDER,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {hero}
                {heroSuffix ? (
                  <span className="fs-5 fw-semibold ms-1" style={{ color: CARD_MUTED }}>
                    {heroSuffix}
                  </span>
                ) : null}
              </span>
            </div>
          )}
          {stats.length > 0 && (
            <ul className="list-unstyled mb-0 small flex-grow-1" style={{ color: CARD_MUTED }}>
              {stats.map((row, i) => (
                <li
                  key={`${row.label}-${i}`}
                  className={`d-flex justify-content-between gap-2 py-1 ${i > 0 ? 'border-top border-light pt-2 mt-1' : ''}`}
                >
                  <span className="d-inline-flex align-items-center flex-wrap">
                    {row.label}
                    <StatHint text={row.hint} hintKey={i} />
                  </span>
                  <span className="fw-semibold text-dark" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {row.value}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

/** Drop-off bars: width vs first stage; shows % lost from previous step */
function FunnelDropOffChart({ stages }) {
  if (!stages?.length) return null;
  const baseline = stages[0]?.count || 0;
  return (
    <div className="mt-1">
      {stages.map((s, idx) => {
        const widthPct = baseline > 0 ? Math.min(100, (s.count / baseline) * 100) : 0;
        return (
          <div key={s.id} className="mb-3">
            <div className="d-flex justify-content-between align-items-baseline gap-2 mb-1">
              <span className="small" style={{ color: CARD_MUTED, maxWidth: '58%' }}>
                {s.label}
              </span>
              <span className="small fw-semibold text-dark" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatNumber(s.count)}
              </span>
            </div>
            <div
              className="rounded-pill overflow-hidden"
              style={{ height: 16, background: 'rgba(32, 92, 143, 0.1)' }}
            >
              <div
                className="h-100 rounded-pill"
                title={`${widthPct.toFixed(1)}% of top stage`}
                style={{
                  width: `${widthPct}%`,
                  background: MED_MANAGEMENT_BORDER,
                  minWidth: s.count > 0 ? 3 : 0,
                  transition: 'width 0.25s ease',
                }}
              />
            </div>
            {idx > 0 && s.drop_from_previous_pct != null && (
              <div className="mt-1 text-muted" style={{ fontSize: '0.65rem' }}>
                Drop from previous: {s.drop_from_previous_pct}%
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const MISSING_CELL_BG = '#fff9c4';

function isMissingCellValue(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string' && v.trim() === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  if (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0) {
    return true;
  }
  return false;
}

function displayCellValue(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function columnLabel(key) {
  if (key === 'appointment_id') return 'Appointment ID';
  if (key === 'appointment_start_time') return 'Appointment start time';
  return key.replace(/_/g, ' ');
}

/** Full opm_patients.* plus appointment context; missing/null cells highlighted */
function UnmatchedOpmPatientsTable({ rows, total, truncated }) {
  if (!rows?.length) return null;
  const cap = 500;
  const summary =
    truncated && total != null ? (
      <span className="text-muted fw-normal ms-1">
        Showing {rows.length} of {total} (cap {cap})
      </span>
    ) : total != null ? (
      <span className="text-muted fw-normal ms-1">({total} total)</span>
    ) : null;

  const keySet = new Set();
  rows.forEach((r) => {
    Object.keys(r).forEach((k) => keySet.add(k));
  });
  const priority = ['appointment_id', 'appointment_start_time'];
  const rest = [...keySet].filter((k) => !priority.includes(k)).sort();
  const columns = [...priority.filter((k) => keySet.has(k)), ...rest];

  return (
    <div className="mt-3 pt-2 border-top">
      <div className="small fw-semibold mb-2" style={{ color: MED_MANAGEMENT_BORDER }}>
        OPM patients not matched to Springhouse (full <code>opm_patients</code> row){' '}
        <StatHint text="OPM appointment in range with an opm_patients row, but no patient row where patient.external_id = phoenix_id. Includes all columns from opm_patients plus appointment_id and appointment_start_time." hintKey="opm-unmatched" />
        {summary}
      </div>
      <div
        className="border rounded"
        style={{ maxHeight: 'min(55vh, 520px)', overflow: 'auto' }}
      >
        <Table size="sm" className="mb-0 small align-middle" style={{ minWidth: 'max-content' }}>
          <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              {columns.map((col) => (
                <th key={col} className="text-nowrap">
                  {columnLabel(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`unmatched-opm-${i}`}>
                {columns.map((col) => {
                  const val = r[col];
                  const missing = isMissingCellValue(val);
                  return (
                    <td
                      key={col}
                      className="text-break"
                      style={{
                        backgroundColor: missing ? MISSING_CELL_BG : undefined,
                        maxWidth: 240,
                        verticalAlign: 'top',
                      }}
                      title={missing ? 'Missing / empty' : undefined}
                    >
                      {missing ? '—' : displayCellValue(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function NoteBreakdownContent({ data }) {
  if (!data || data.error) return null;
  const addPct = data.addition_by_category_percent || {};
  const subPct = data.subtraction_by_category_percent || {};
  const addCnt = data.addition_by_category_counts || {};
  const subCnt = data.subtraction_by_category_counts || {};
  const keys = Object.keys({ ...addPct, ...subPct });

  return (
    <div className="mt-2">
      <p className="small text-muted mb-2 mb-md-3">
        Structured line items (operations with at least one category). Addition vs subtraction:{' '}
        <strong>{formatNumber(data.percent_addition_vs_subtraction?.addition)}%</strong> add,{' '}
        <strong>{formatNumber(data.percent_addition_vs_subtraction?.subtraction)}%</strong> subtract.
      </p>
      <Table size="sm" responsive className="mb-0 small align-middle" hover>
        <thead className="table-light">
          <tr>
            <th>Category</th>
            <th className="text-end">Add #</th>
            <th className="text-end">Add %</th>
            <th className="text-end">Sub #</th>
            <th className="text-end">Sub %</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k}>
              <td className="text-capitalize">{k.replace(/_/g, ' ')}</td>
              <td className="text-end">{formatNumber(addCnt[k])}</td>
              <td className="text-end">{formatNumber(addPct[k])}%</td>
              <td className="text-end">{formatNumber(subCnt[k])}</td>
              <td className="text-end">{formatNumber(subPct[k])}%</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function isFullMatchPercent(pct) {
  if (pct === null || pct === undefined) return false;
  const n = Number(pct);
  return n === 100 || (n >= 99.99 && n <= 100);
}

function renderMetricScoreboard(id, block) {
  if (block.error) {
    return (
      <div key={id} className="col-md-6 col-xl-4">
        <div className="h-100 rounded-3 border border-danger border-opacity-25 p-3 bg-danger bg-opacity-10">
          <div className="small fw-semibold text-danger mb-1">{block.label || id}</div>
          <div className="small text-danger">{block.error}</div>
        </div>
      </div>
    );
  }

  const d = block.data || {};

  switch (id) {
    case 'avg_recommendations_per_user':
      return (
        <MetricScoreCard
          key={id}
          title={block.label}
          subtitle="Distinct recommendation_id ÷ distinct patient_id (created in range)"
          hero={formatNumber(d.avg_recommendations_per_user)}
          stats={[
            { label: 'Distinct recommendations', value: formatNumber(d.distinct_recommendations) },
            { label: 'Distinct patients', value: formatNumber(d.distinct_patients) },
          ]}
        />
      );

    case 'active_recommendation_appointment_alignment': {
      const pct = d.percent_patients_matching;
      const perfect = isFullMatchPercent(pct);
      return (
        <MetricScoreCard
          key={id}
          title={block.label}
          subtitle="OPM: start_time in range (booked/scheduled). Recs: active, created_at in range, appt ≠ 0."
          hero={formatNumber(pct)}
          heroSuffix="%"
          highlightSuccess={perfect}
          stats={[
            { label: 'Count of processed OPM appointments', value: formatNumber(d.distinct_recommendation_appointments) },
            { label: '# OPM patients scheduled with >1 appointment', value: formatNumber(d.patients_with_more_than_one_opm_appointment) },
            { label: 'Patients in scope (rec alignment)', value: formatNumber(d.patients) },
            {
              label: 'Patients with matching rec/appt counts',
              value: formatNumber(d.patients_where_counts_match),
              hint:
                'A patient can have multiple valid appointments scheduled at one time. This metric measures that every patient has a the same number of active recommendations as appointments.',
            },
            { label: 'Avg. |difference|', value: formatNumber(d.avg_absolute_difference) },
          ]}
        />
      );
    }

    case 'avg_notes_per_patient':
      return (
        <MetricScoreCard
          key={id}
          title={block.label}
          subtitle="Distinct note patient_id ÷ distinct patients in recommendations (same range)"
          hero={formatNumber(d.avg_notes_per_patient)}
          stats={[
            { label: 'Distinct patients (patient_notes)', value: formatNumber(d.distinct_patients_with_notes) },
            { label: 'Distinct patients (recommendations)', value: formatNumber(d.distinct_patients_in_recommendations) },
          ]}
        />
      );

    case 'synthetic_surgery_recommendations':
      return (
        <MetricScoreCard
          key={id}
          title={block.label}
          subtitle="Distinct recommendations · appointment_id = 0"
          hero={formatNumber(d.distinct_recommendations_appointment_id_zero)}
        />
      );

    case 'text_feature_uses':
      return (
        <MetricScoreCard
          key={id}
          title={block.label}
          subtitle="Notes with non-empty text"
          hero={formatNumber(d.notes_with_non_empty_text)}
        />
      );

    case 'note_addition_subtraction_breakdown':
      return (
        <MetricScoreCard
          key={id}
          title={block.label}
          subtitle="By medication, events, conditions, risk, surgery"
          wide
          hero={formatNumber(d.structured_operations_in_range)}
          heroSuffix="ops"
          stats={[
            { label: 'Addition operations', value: formatNumber(d.addition_operations) },
            { label: 'Subtraction operations', value: formatNumber(d.subtraction_operations) },
          ]}
        >
          <NoteBreakdownContent data={d} />
        </MetricScoreCard>
      );

    case 'openpm_phoenix_recommendation_funnel': {
      const stages = d.stages || [];
      const top = stages[0]?.count;
      return (
        <MetricScoreCard
          key={id}
          wide
          title={block.label}
          subtitle="Distinct OPM appointments (start_time in range). Each step must be non-null to count."
          hero={formatNumber(top)}
          heroSuffix=" appts"
        >
          <FunnelDropOffChart stages={stages} />
          <UnmatchedOpmPatientsTable
            rows={d.unmatched_opm_patients}
            total={d.unmatched_opm_patients_total}
            truncated={d.unmatched_opm_patients_truncated}
          />
        </MetricScoreCard>
      );
    }

    default:
      return (
        <MetricScoreCard key={id} title={block.label || id} wide>
          <pre className="small mb-0 text-muted" style={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(d, null, 2)}
          </pre>
        </MetricScoreCard>
      );
  }
}

function CalendarRangeMetricsCards({ medManagementStats, jointDecisionsStats, rangeLabelShort }) {
  return (
    <div className="row g-3 mb-3">
      <div className="col-md-5">
        <div className="card h-100" style={{ borderColor: JOINT_DECISIONS_COLOR, borderWidth: 2 }}>
          <div className="card-header" style={{ backgroundColor: JOINT_DECISIONS_COLOR }}>
            <strong>Joint Decisions</strong>
          </div>
          <div className="card-body">
            <div className="text-center py-4">
              <span className="badge bg-secondary mb-2">Coming Soon</span>
              <p className="text-muted small mb-3">Risk profiles for all incoming patients.</p>
              <div className="row g-2 text-start">
                <div className="col-6">
                  <div className="p-2 rounded" style={{ backgroundColor: 'rgba(198, 218, 240, 0.4)' }}>
                    <div className="h5 mb-0" style={{ color: '#205c8f' }}>
                      {jointDecisionsStats.count}
                    </div>
                    <small className="text-muted">Incoming patients {rangeLabelShort}</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-2 rounded border" style={{ opacity: 0.6 }}>
                    <div className="h6 mb-0 text-muted">Coming soon</div>
                    <small className="text-muted">Requiring more info</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-7">
        <div className="card h-100" style={{ borderColor: MED_MANAGEMENT_COLOR, borderWidth: 2 }}>
          <div className="card-header" style={{ backgroundColor: MED_MANAGEMENT_COLOR }}>
            <strong>Med Management</strong>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-6 col-md-4">
                <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(240, 198, 198, 0.3)' }}>
                  <div className="display-6 fw-bold" style={{ color: '#8F2020' }}>
                    {medManagementStats.surgeries.length}
                  </div>
                  <small className="text-muted">Surgeries {rangeLabelShort}</small>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center justify-content-center">
                  {medManagementStats.pieData.length > 0 ? (
                    <SurgeryTypePieChart data={medManagementStats.pieData} />
                  ) : (
                    <div className="text-muted small">No surgery types</div>
                  )}
                </div>
                <small className="text-muted d-block text-center">Surgery types {rangeLabelShort}</small>
              </div>
              <div className="col-6 col-md-4">
                <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(245, 238, 158, 0.4)' }}>
                  <div className="display-6 fw-bold text-warning">{medManagementStats.requiringAction}</div>
                  <small className="text-muted">Requiring action</small>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(92, 153, 110, 0.2)' }}>
                  <div className="display-6 fw-bold text-success">{medManagementStats.approved}</div>
                  <small className="text-muted">Approved</small>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(245, 238, 158, 0.3)' }}>
                  <div className="display-6 fw-bold" style={{ color: '#b8860b' }}>
                    {medManagementStats.modified}
                  </div>
                  <small className="text-muted">Modified</small>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(220, 53, 69, 0.15)' }}>
                  <div className="display-6 fw-bold text-danger">{medManagementStats.denied}</div>
                  <small className="text-muted">Denied</small>
                </div>
              </div>
            </div>
            {medManagementStats.pieData.length > 0 && (
              <div className="mt-2 d-flex flex-wrap gap-2">
                {[...medManagementStats.pieData]
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((d) => (
                  <span
                    key={d.label}
                    className="badge"
                    style={{
                      backgroundColor: getSurgeryTypeColor(d.label, {
                        labels: medManagementStats.pieData.map((p) => p.label),
                      }),
                    }}
                  >
                    {d.label}: {d.count}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Expandable analytics panel; uses the same calendar range as AppointmentsCalendar (dateRange).
 */
function CalendarAnalyticsDashboard({
  dateRange,
  medManagementStats,
  jointDecisionsStats,
  rangeLabelShort,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const load = useCallback(async () => {
    if (!dateRange?.start || !dateRange?.end) return;
    setLoading(true);
    setError(null);
    try {
      const startIso = dateRange.start.toISOString();
      const endExclusive = new Date(dateRange.end.getTime() + 1).toISOString();
      const data = await fetchDashboardAnalytics(startIso, endExclusive);
      if (!data) {
        setError('Sign in to load analytics.');
        setPayload(null);
        return;
      }
      setPayload(data);
    } catch (e) {
      setError(e?.message || 'Failed to load analytics');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  const rangeLabel = dateRange?.start && dateRange?.end
    ? `${dateRange.start.toLocaleDateString()} – ${dateRange.end.toLocaleDateString()}`
    : '';

  return (
    <div className="mt-4">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          {open ? 'Hide analytics dashboard' : 'Load more analytics'}
        </Button>
        {open && (
          <Button variant="link" size="sm" className="p-0" onClick={load} disabled={loading}>
            Refresh
          </Button>
        )}
        {open && rangeLabel && (
          <small className="text-muted">Range: {rangeLabel} (by created time)</small>
        )}
      </div>

      <Collapse in={open}>
        <div>
          {medManagementStats && jointDecisionsStats && rangeLabelShort != null && (
            <CalendarRangeMetricsCards
              medManagementStats={medManagementStats}
              jointDecisionsStats={jointDecisionsStats}
              rangeLabelShort={rangeLabelShort}
            />
          )}
          <div
            className="card border-0 shadow-sm"
            style={{ border: `1px solid ${MED_MANAGEMENT_COLOR}` }}
          >
            <div
              className="card-header py-3 border-0 rounded-top"
              style={{
                background: `linear-gradient(135deg, ${MED_MANAGEMENT_COLOR} 0%, #e8f0fa 100%)`,
              }}
            >
              <strong className="d-block" style={{ color: MED_MANAGEMENT_BORDER }}>
                Analytics dashboard
              </strong>
              <div className="small text-muted mt-1">
                Filtered by <code>created_at</code> within the visible calendar range.
              </div>
            </div>
            <div className="card-body pt-4 bg-light bg-opacity-50">
              {loading && (
                <div className="d-flex align-items-center gap-2 text-muted py-4 justify-content-center">
                  <Spinner animation="border" size="sm" />
                  Loading…
                </div>
              )}
              {!loading && error && <div className="text-danger small">{error}</div>}
              {!loading && !error && payload?.metrics && (
                <div className="row g-3">
                  {Object.entries(payload.metrics).map(([id, block]) => renderMetricScoreboard(id, block))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
}

export default CalendarAnalyticsDashboard;
